/**
 * Sprint 35 chunk 9 — "What CARLA did" panel for the TriageActionCard.
 *
 * Renders only when item.assistant_processed_at is non-null. Shows:
 *   - assistant_summary (the agent's 2-3 sentence self-report)
 *   - assistant_kind chip (Orders / Support)
 *   - Expandable tool-call list pulled from /maildesk-triage/items/{id}/audit
 *
 * The retry button lives in TriageActionCard's action row (not here)
 * so the existing layout stays consistent.
 */
import { useState } from 'react'
import { ChevronDown, ChevronUp, Bot, RefreshCw } from 'lucide-react'
import { Button, Chip } from '@/components/ui'
import type { TriageItem } from '../api/types'
import { useAssistantAudit, type AuditEntry } from '../api/queries'
import {
  ASSISTANT_STATUS_LABEL,
  ASSISTANT_STATUS_VARIANT,
} from '../utils/triage-meta'

interface Props {
  item: TriageItem
  onRunAssistant: (id: string) => void
  /** Show the retry button (admin / operations roles only). */
  canRetry: boolean
  /** Mutation in-flight on this row. */
  busy?: boolean
}

function formatRelative(iso: string | null): string {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return iso
  const minutesAgo = Math.floor((Date.now() - t) / 60_000)
  if (minutesAgo < 1) return 'just now'
  if (minutesAgo < 60) return `${minutesAgo} min ago`
  const hoursAgo = Math.floor(minutesAgo / 60)
  if (hoursAgo < 24) return `${hoursAgo}h ago`
  const daysAgo = Math.floor(hoursAgo / 24)
  return `${daysAgo}d ago`
}

export function AssistantPanel({ item, onRunAssistant, canRetry, busy }: Props) {
  const [expanded, setExpanded] = useState(false)
  const auditQuery = useAssistantAudit(item.id, expanded)

  // Hidden when CARLA has never touched the row.
  if (!item.assistant_processed_at || !item.assistant_status) return null

  return (
    <div className="mt-3 rounded-md border border-info-border bg-info-bg p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Bot size={14} />
            <span className="text-role-section-label">What CARLA did</span>
            <Chip
              size="xs"
              variant={ASSISTANT_STATUS_VARIANT[item.assistant_status]}
            >
              {ASSISTANT_STATUS_LABEL[item.assistant_status]}
            </Chip>
            {item.assistant_kind ? (
              <span className="text-role-metadata">
                · {item.assistant_kind === 'orders' ? 'Orders agent' : 'Support agent'}
              </span>
            ) : null}
            <span className="text-role-metadata">
              · {formatRelative(item.assistant_processed_at)}
            </span>
          </div>
          {item.assistant_summary ? (
            <p className="text-role-data-secondary mt-2">{item.assistant_summary}</p>
          ) : (
            <p className="text-role-metadata mt-2 italic">(no summary written)</p>
          )}
        </div>
        {canRetry ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRunAssistant(item.id)}
            disabled={busy}
            title="Re-run the inbox assistant on this row"
          >
            <RefreshCw size={12} /> Re-run
          </Button>
        ) : null}
      </div>

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="text-role-metadata mt-3 inline-flex items-center gap-1 hover:underline"
      >
        {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        {expanded ? 'Hide' : 'Show'} tool calls
      </button>

      {expanded ? (
        <AuditList
          loading={auditQuery.isLoading}
          error={auditQuery.error ? String(auditQuery.error) : null}
          entries={auditQuery.data?.entries ?? []}
        />
      ) : null}
    </div>
  )
}

interface AuditListProps {
  loading: boolean
  error: string | null
  entries: AuditEntry[]
}

function AuditList({ loading, error, entries }: AuditListProps) {
  if (loading) {
    return (
      <p className="text-role-metadata mt-2 italic">Loading audit log…</p>
    )
  }
  if (error) {
    return (
      <p className="text-role-metadata mt-2 text-danger">
        Failed to load audit log: {error}
      </p>
    )
  }
  if (!entries.length) {
    return (
      <p className="text-role-metadata mt-2 italic">
        No tool calls recorded yet.
      </p>
    )
  }

  // agent_audit_log returns newest-first; reverse so the timeline
  // reads chronologically (first tool called → last).
  const ordered = [...entries].reverse()

  return (
    <ol className="mt-2 space-y-1">
      {ordered.map((e) => (
        <li
          key={e.id}
          className="text-role-metadata flex items-start gap-2 border-l border-border-subtle pl-2"
        >
          <span className="font-sans tabular-nums opacity-60">
            {formatRelative(e.created_at)}
          </span>
          <span className="text-role-data-secondary">
            {e.tool_name ?? '(no tool — loop_suspected?)'}
          </span>
          {e.status !== 'success' ? (
            <Chip size="xs" variant={statusVariant(e.status)}>
              {e.status}
            </Chip>
          ) : null}
          {e.latency_ms != null ? (
            <span className="opacity-60">{e.latency_ms}ms</span>
          ) : null}
          {e.result_summary ? (
            <span className="truncate opacity-75" title={e.result_summary}>
              — {e.result_summary}
            </span>
          ) : null}
        </li>
      ))}
    </ol>
  )
}

function statusVariant(
  status: string,
): 'success' | 'warning' | 'info' | 'neutral' | 'danger' {
  switch (status) {
    case 'success':
      return 'success'
    case 'denied':
      return 'warning'
    case 'failure':
    case 'loop_suspected':
      return 'danger'
    default:
      return 'neutral'
  }
}
